import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, filter, take, Observable } from 'rxjs';
import { captureException, captureMessage } from '../services/sentry-client';
import { isMappedBusinessErrorCode } from '../errors/business-error-messages';
import { GzToastService } from '@gozem/ui';
import { AuthStore } from '../stores/auth-store';
import { AuthApi } from '../apis';
import { Storage, TokenRefreshService } from '../services';
import { RouteNavigator } from '../services/route-navigator.service';
import { ACCOUNT_SUSPENDED_MESSAGE, ErrorCode, normalizeErrorCode } from '../models';
import type { RefreshTokenResponse } from '../models';
import { extractErrorMessage } from '@shared/utils/http-error.util';
import {
  currentAppUrl,
  shouldRedirectToLogin,
  toReturnUrlParam,
} from '@shared/utils/return-url.util';

/**
 * Auth Interceptor - Handles JWT authentication and automatic token refresh
 *
 * Token Strategy:
 * - All APIs use dynamic JWT access_token (expires in 1hr, auto-refresh)
 *
 * Flow:
 * 1. Attach Authorization header with access_token from AuthStore
 * 2. On 401 errors: refresh token and retry the request
 * 3. If refresh fails: clear session and redirect to login
 *
 * Security:
 * - Prevents multiple simultaneous refresh requests (via TokenRefreshService)
 * - Queues failed requests during refresh and replays them with the new token
 * - Refresh token stored in localStorage (cleared on logout)
 * - TokenRefreshService.reset() is called on logout to prevent stale state across sessions
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const authApi = inject(AuthApi);
  const storage = inject(Storage);
  const router = inject(Router);
  const location = inject(Location);
  const routeNavigator = inject(RouteNavigator);
  const tokenRefresh = inject(TokenRefreshService);
  const toast = inject(GzToastService);

  const url = req.url;

  // Skip public endpoints (no authentication required)
  if (isPublicEndpoint(url)) {
    return next(req);
  }

  // Get dynamic access token from AuthStore
  const token = authStore.getAccessToken();

  // Clone request with Authorization header — skip if the request already sets its own
  let clonedRequest = req;
  if (token && !req.headers.has('Authorization')) {
    clonedRequest = addTokenToRequest(req, token);
  }

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized errors - attempt token refresh
      if (error.status === 401) {
        return handle401Error({
          request: clonedRequest,
          next,
          authStore,
          authApi,
          storage,
          router,
          location,
          routeNavigator,
          tokenRefresh,
        });
      }

      // Suspended account (error_code "045"): surface a clear suspension message and
      // let the error propagate so the in-flight transaction aborts. Covers every
      // endpoint uniformly (auth_txn for topup/send/withdraw, payments, etc.).
      if (isSuspensionError(error)) {
        toast.show({
          tone: 'danger',
          message: extractErrorMessage(error, ACCOUNT_SUSPENDED_MESSAGE),
        });
      }

      reportUnmappedBusinessError(error);

      return throwError(() => error);
    }),
  );
};

/**
 * Detect a suspended-account rejection (HTTP 400, business error_code "045").
 */
function isSuspensionError(error: HttpErrorResponse): boolean {
  const code = normalizeErrorCode((error.error as { error_code?: unknown } | null)?.error_code);
  return error.status === 400 && code === ErrorCode.ACCOUNT_SUSPENDED;
}

/**
 * Report a coded business error we have no French copy for.
 *
 * This is the interceptor's job rather than the registry's because only here do we
 * still have the endpoint that produced it — the code alone is not enough to act on.
 *
 * Why it exists: before this, nothing told us a new backend code had reached a user.
 * Code "1032" was found by a QA engineer clicking through staging, and the user saw
 * "Une erreur est survenue" in the meantime. An unmapped code is a gap in OUR copy,
 * so it should page us, not the user.
 *
 * Deliberately gated on a present code: network failures and uncoded 5xx crashes are
 * already reported through other paths and would only add noise here.
 */
function reportUnmappedBusinessError(error: HttpErrorResponse): void {
  const rawCode = (error.error as { error_code?: unknown } | null)?.error_code;
  const code = normalizeErrorCode(rawCode);
  if (code === null || isMappedBusinessErrorCode(code)) {
    return;
  }

  captureMessage(`Unmapped business error code ${code}`, {
    level: 'warning',
    tags: { category: 'api', business_error_unmapped: code },
    extra: { code, status: error.status, url: error.url },
  });
}

/**
 * Check if endpoint is public (no authentication required)
 */
function isPublicEndpoint(url: string): boolean {
  const publicEndpoints = [
    '/auth/login',
    '/auth/verify-merchant-login',
    '/auth/forgot-password',
    '/auth/refresh-token',
    // Phase 2 of login: the caller has authenticated but holds no token yet, so there is
    // nothing to attach. Left off this list it would carry a Bearer from a half-built or
    // previous session, and a 401 would kick off a refresh against a refresh token that
    // does not exist — surfacing as a forced logout instead of a readable error.
    '/account/session',
  ];

  return publicEndpoints.some((endpoint) => url.includes(endpoint));
}

/**
 * Add authorization header to request
 */
function addTokenToRequest(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/** Collaborators `handle401Error` needs, passed as one object (SonarQube S107). */
interface Handle401Params {
  readonly request: HttpRequest<unknown>;
  readonly next: HttpHandlerFn;
  readonly authStore: AuthStore;
  readonly authApi: AuthApi;
  readonly storage: Storage;
  readonly router: Router;
  readonly location: Location;
  readonly routeNavigator: RouteNavigator;
  readonly tokenRefresh: TokenRefreshService;
}

/**
 * Send a user whose session just died to the login form, remembering where they
 * were headed.
 *
 * Two rules this encodes, both learned from the redirect it replaces
 * (`{ returnUrl: router.url }`):
 *
 * 1. **The destination comes from `currentAppUrl`, never `Router.url`.** A 401
 *    handled before the router commits its first navigation — from the auth app
 *    initializer, or from `configResolver`'s fire-and-forget config load — reads
 *    `Router.url` as `"/"`, because `currentUrlTree` is only assigned at route
 *    activation. That produced `/login?returnUrl=%2F`, losing the deep link and
 *    landing the user on the public marketing page after signing back in.
 * 2. **Public pages are left alone.** Bailing out here also avoids a subtler
 *    failure: `Router.initialNavigation()` skips syncing to the browser URL once
 *    any navigation has been requested, so redirecting during bootstrap
 *    *cancels* the navigation the user actually asked for.
 */
function redirectToLogin({
  router,
  location,
  routeNavigator,
}: Pick<Handle401Params, 'router' | 'location' | 'routeNavigator'>): void {
  const current = currentAppUrl(router, location);

  if (!shouldRedirectToLogin(current)) {
    return;
  }

  const returnUrl = toReturnUrlParam(current);
  routeNavigator.to(['/login'], { queryParams: returnUrl ? { returnUrl } : {} });
}

/**
 * Handle 401 Unauthorized error.
 * Either starts a new refresh (if none is in progress) or queues this request
 * behind the in-progress refresh via TokenRefreshService.token$.
 */
function handle401Error({
  request,
  next,
  authStore,
  authApi,
  storage,
  router,
  location,
  routeNavigator,
  tokenRefresh,
}: Handle401Params): Observable<HttpEvent<unknown>> {
  if (!tokenRefresh.isRefreshing) {
    tokenRefresh.start();

    const refreshToken = storage.getRefreshToken();

    if (!refreshToken) {
      tokenRefresh.fail();
      captureException(new Error('No refresh token available — user forced to logout'), {
        tags: { category: 'auth', action: 'missing-refresh-token' },
      });
      authStore.clearSession();
      storage.clearRefreshToken();
      redirectToLogin({ router, location, routeNavigator });
      return throwError(() => new Error('No refresh token available'));
    }

    return authApi.refresh(refreshToken).pipe(
      switchMap((response: RefreshTokenResponse) => {
        authStore.updateAccessToken(response.data.access_token, response.data.expires_in);
        tokenRefresh.complete(response.data.access_token);
        return next(addTokenToRequest(request, response.data.access_token));
      }),
      catchError((error: HttpErrorResponse) => {
        tokenRefresh.fail();
        captureException(error, {
          tags: { category: 'auth', action: 'token-refresh-failed' },
          extra: { status: error.status, url: error.url },
        });
        authStore.clearSession();
        storage.clearRefreshToken();
        redirectToLogin({ router, location, routeNavigator });
        return throwError(() => error);
      }),
    );
  }

  // Refresh already in progress — queue this request until the new token arrives
  return tokenRefresh.token$.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => next(addTokenToRequest(request, token))),
  );
}
